import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider
const Tooltip = TooltipPrimitive.Root
const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      // collisionPadding keeps it inside the viewport rather than pushing the
      // page sideways — a tooltip on the right-hand column of a 390px screen
      // would otherwise be the thing that makes iOS Safari zoom out.
      collisionPadding={8}
      className={cn(
        "z-50 max-w-[min(20rem,calc(100vw-1rem))] overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md",
        "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

/**
 * A label that explains a control, on hover AND on tap.
 *
 * Radix opens on hover and on keyboard focus, which covers a mouse and a
 * keyboard but not a finger — there is no hover on a touchscreen, so every
 * hover-only explanation is simply missing on a phone. Passing the tooltip's
 * open state through here as well lets a tap toggle it, so the same one line
 * of help is reachable at every width. That is the whole reason this wrapper
 * exists rather than callers composing the primitives themselves.
 *
 * `label` is also written to `aria-label`/`title` on the trigger when the
 * child has no text of its own, so an icon button still announces itself to a
 * screen reader and still shows the OS tooltip if JavaScript hasn't hydrated.
 */
export function InfoTip({
  label,
  children,
  side = "top",
  align = "center",
  delayDuration = 200,
  asChild = true,
  className,
}: {
  label: React.ReactNode
  children: React.ReactNode
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  delayDuration?: number
  asChild?: boolean
  className?: string
}) {
  const [open, setOpen] = React.useState(false)
  // Radix closes a tooltip on press, which on a touchscreen is the very
  // gesture meant to open it — so a tap opened and instantly re-closed it,
  // and the phone got no explanation at all. Ignoring Radix's close for a
  // moment after a touch is what makes the tap stick. A mouse is unaffected:
  // it never sets this.
  const touchAt = React.useRef(0)

  return (
    <Tooltip
      open={open}
      onOpenChange={(next) => {
        if (!next && Date.now() - touchAt.current < 400) return
        setOpen(next)
      }}
      delayDuration={delayDuration}
    >
      <TooltipTrigger
        asChild={asChild}
        // A finger produces no hover, so the tap has to open it. This fires
        // before the click, and does not preventDefault, so a button that
        // both does something and explains itself still does the thing.
        onPointerDown={(event) => {
          if (event.pointerType === "mouse") return
          touchAt.current = Date.now()
          setOpen((v) => !v)
        }}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent side={side} align={align} className={className}>
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
